import * as React from 'react'
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import History from "@tiptap/extension-history";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "./text-align";
import FontSize from "./font-size";
import FontColor from "./font-color";
import { MMOOptions } from '../view_2/mmo-interactor';
import { contentAsAttrArray, flbArrayAsContent, TipTapDoc } from './helpers';
import TextFlowObject from '../../model/TextFlowObject';
import { MachineContext } from '../fsm/useMachine';
import { useEditorCoordinator } from './editorCoordinator';
import { DELEGATE, EDIT_TEXT, MARK_EDITOR_ACTIVE, MARK_INACTIVE, SET_CURRENT_OBJECT, SET_SIZE } from '../fsm/types';
import _debounce from "lodash.debounce"

export interface TextEditorOptions {
    mmo: TextFlowObject
    editOptions: [{[key: string]: boolean}, React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>]
}

export const TextEditor: React.FC<TextEditorOptions & MMOOptions> = (props) => {
    const [current, send] = React.useContext(MachineContext)
    const { globalEditor, registerDebouncedUpdate, unregisterDebouncedUpdate } = useEditorCoordinator()
    const [, setGlobalEditor] = globalEditor
    const { mmo, editOptions, size, setShowTransformer } = props;
    const [allowEdit, ] = editOptions
    const doc = flbArrayAsContent(mmo.attributedString_flbAttributesArray, mmo.text, props.size.ratio)

    // Keep a ref to the latest mmo so the debounced closure always has current values
    const mmoRef = React.useRef(mmo);
    mmoRef.current = mmo;

    // Store debounced function in ref so we can flush it
    const debouncedUpdateRef = React.useRef<ReturnType<typeof _debounce> | null>(null)
    if (!debouncedUpdateRef.current) {
        debouncedUpdateRef.current = _debounce(({ editor }) => {
            const currentMmo = mmoRef.current;
            const { text, fontAttributes } = contentAsAttrArray(editor.getJSON() as TipTapDoc, size.ratio)
            if(text && text.length > 1){
                send({
                    type: DELEGATE, payload: {
                        type: EDIT_TEXT,
                        attributes: fontAttributes,
                        text,
                        isThemeText: false
                    }, id: currentMmo.id
                })

                // Auto-resize: measure ProseMirror content height
                const prose = props.forwardedRef.current?.querySelector('.ProseMirror') as HTMLElement | null;
                if (prose) {
                    const prevOverflow = prose.style.overflow;
                    prose.style.overflow = 'visible';
                    const contentHeight = prose.scrollHeight;
                    prose.style.overflow = prevOverflow;

                    // Use actual rendered container height (avoids stale closure on mmo.frame)
                    const container = props.forwardedRef.current;
                    const currentRenderedHeight = container ? container.offsetHeight : 0;

                    if (currentRenderedHeight > 0 && Math.abs(contentHeight - currentRenderedHeight) > 2) {
                        const newHeight = contentHeight / size.ratio;
                        send({
                            type: DELEGATE,
                            payload: { type: SET_SIZE, width: currentMmo.frame.width, height: newHeight },
                            id: currentMmo.id
                        });
                    }
                }
            }
        }, 300)
    }

    // Register this debounced update with the coordinator
    React.useEffect(() => {
        if (debouncedUpdateRef.current) {
            registerDebouncedUpdate(mmo.id, debouncedUpdateRef.current)
        }
        return () => {
            unregisterDebouncedUpdate(mmo.id)
        }
    }, [mmo.id, registerDebouncedUpdate, unregisterDebouncedUpdate])

    const editor = useEditor({
        extensions: [
            Document,
            Paragraph,
            Text,
            TextStyle,
            FontFamily,
            FontSize,
            FontColor,
            History,
            TextAlign,
        ],
        content: doc,
        onUpdate: debouncedUpdateRef.current,
        editable: false,
    });

    const singleClick = (e: React.MouseEvent<HTMLElement | SVGSVGElement>) => {
            // Don't handle click here - let it bubble up to MMOInteractivity
    }

    React.useEffect(() => {        
        if (current.context.currentObject?.id === mmo.id) {
            setGlobalEditor(editor)
        }
    }, [current.context.currentObject?.id])

    React.useEffect(() => {
        if (!editor) {
            return
        }

        const wasEditing = editor.isEditable
        const willEdit = allowEdit[mmo.id] || false

        // If transitioning from editing to not editing, flush any pending updates
        if (wasEditing && !willEdit && debouncedUpdateRef.current) {
            debouncedUpdateRef.current.flush()
        }

        editor.setEditable(willEdit)
        if (willEdit) {
            send({ type: MARK_EDITOR_ACTIVE })
        } else {
            send({ type: MARK_INACTIVE })
        }
    }, [allowEdit[mmo.id], editor])

    React.useEffect(() => {
        // Update editor content when mmo text/attributes change
        // BUT only if the editor is not currently being edited
        // This prevents cursor jumping when typing and fixes Firefox/Brave issue where
        // editor.isFocused doesn't work reliably (text reverts to default during debounce)
        if (editor && !editor.isDestroyed && !allowEdit[mmo.id]) {
            const newDoc = flbArrayAsContent(mmo.attributedString_flbAttributesArray, mmo.text, props.size.ratio)
            editor.commands.setContent(newDoc, false)
        }
    }, [mmo.text, mmo.attributedString_flbAttributesArray])

    React.useEffect(() => {
        const prose = props.forwardedRef.current.querySelector(".ProseMirror")
        if (props.forwardedRef.current && prose) {
            prose.style.overflow = "hidden";
            prose.id = mmo.id
        }
    }, [props.forwardedRef.current])

    // Flush pending updates when screen index changes (BEFORE component unmounts)
    React.useEffect(() => {
        return () => {
            // When screen changes, this cleanup runs BEFORE Text.tsx unmounts
            if (debouncedUpdateRef.current && editor && !editor.isDestroyed) {
                debouncedUpdateRef.current.flush()
            }
        }
    }, [current.context.index])

    const isEditing = allowEdit[mmo.id] || false
    return <EditorContent
        editor={editor}
        onClick={singleClick}
        style={{ pointerEvents: isEditing ? 'auto' : 'none' }}
    />
}